import React, { useState } from "react";
import { Search } from "lucide-react";
import api, { formatPrice, resolveImg } from "../lib/api";
import { customerStatusLabel } from "../lib/shipping";
import PackageList from "../components/PackageList";
import OrderAdjustments, { SendingNote } from "../components/OrderAdjustments";
import { mtDate } from "../lib/dates";

export default function TrackOrder() {
  const [email, setEmail] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await api.post("/orders/track", { email, order_number: orderNumber });
      setOrder(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Order not found");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 lg:py-24" data-testid="track-page">
      <p className="eyebrow mb-3">Order lookup</p>
      <h1 className="font-serif text-5xl text-[var(--ink-forest)] tracking-tight mb-3">Track your order</h1>
      <p className="text-[var(--muted-fg)] mb-10">
        Enter the email and order number from your confirmation.
      </p>

      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 mb-8">
        <input
          type="email"
          required
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="ck-input"
          data-testid="track-email"
        />
        <input
          type="text"
          required
          placeholder="Order number (DC-XXXXXX)"
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
          className="ck-input"
          data-testid="track-order-number"
        />
        <button
          type="submit"
          disabled={loading}
          className="sm:col-span-2 bg-[var(--terracotta)] text-white rounded-full py-3.5 text-sm font-semibold hover:bg-[var(--terracotta-deep)] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
          data-testid="track-submit-btn"
        >
          <Search className="w-4 h-4" />
          {loading ? "Looking up…" : "Find my order"}
        </button>
      </form>

      {error && (
        <div className="bg-[var(--muted)] text-[var(--ink-forest)] rounded-xl p-4 text-sm" data-testid="track-error">
          {error}
        </div>
      )}

      {order && <OrderView order={order} />}

      <style>{`
        .ck-input {
          width: 100%;
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 0.875rem 1rem;
          font-size: 0.9rem;
          color: var(--ink-forest);
          outline: none;
        }
        .ck-input:focus {
          border-color: var(--terracotta);
        }
      `}</style>
    </div>
  );
}

export function OrderView({ order }) {
  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl p-7 mt-4" data-testid="order-details">
      <div className="flex justify-between flex-wrap gap-3 pb-5 border-b border-[var(--border)]">
        <div>
          <p className="eyebrow">Order</p>
          <p className="font-mono text-lg font-semibold text-[var(--ink-forest)]" data-testid="order-number">
            {order.order_number}
          </p>
        </div>
        <div>
          <p className="eyebrow">Placed</p>
          <p className="text-sm text-[var(--ink-forest)] mt-1" data-testid="order-placed-date">{mtDate(order.created_at)}</p>
        </div>
        <div>
          <p className="eyebrow">Status</p>
          <span
            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mt-1 ${statusColor(order.status)}`}
            data-testid="order-status"
          >
            {customerStatusLabel(order.status)}
          </span>
        </div>
        <div>
          <p className="eyebrow">Total</p>
          <p className="font-semibold text-[var(--ink-forest)]" data-testid="order-total">{formatPrice(order.total)}</p>
        </div>
      </div>

      {order.shipments?.length > 0 && (
        <div className="py-5 border-b border-[var(--border)]">
          <PackageList order={order} />
        </div>
      )}

      <div className="py-5 space-y-3">
        {order.items.map((item, i) => (
          <div key={i} className="flex gap-4 text-sm">
            <img src={resolveImg(item.image_url)} alt="" className="w-14 h-14 rounded-lg object-cover bg-[var(--muted)]" />
            <div className="flex-1">
              <p className="text-[var(--ink-forest)]">{item.name}</p>
              <p className="text-xs text-[var(--muted-fg)]">
                {item.quantity} × {formatPrice(item.price)}
              </p>
              <SendingNote item={item} testid={`order-item-sending-${i}`} />
            </div>
            <p className="text-[var(--ink-forest)] font-medium">{formatPrice(item.line_total)}</p>
          </div>
        ))}
      </div>

      {(order.swaps?.length > 0 || order.refunds?.length > 0) && (
        <div className="pb-5">
          <OrderAdjustments order={order} />
        </div>
      )}

      <div className="border-t border-[var(--border)] pt-5 grid sm:grid-cols-2 gap-5 text-sm">
        <div>
          <p className="eyebrow mb-2">Shipping to</p>
          <p>{order.shipping_address.name}</p>
          <p className="text-[var(--muted-fg)]">{order.shipping_address.address_line1}</p>
          {order.shipping_address.address_line2 && (
            <p className="text-[var(--muted-fg)]">{order.shipping_address.address_line2}</p>
          )}
          <p className="text-[var(--muted-fg)]">
            {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}
          </p>
        </div>
        <div>
          <p className="eyebrow mb-2">Order totals</p>
          <p className="flex justify-between"><span className="text-[var(--muted-fg)]">Subtotal</span> <span>{formatPrice(order.subtotal)}</span></p>
          {order.discount > 0 && (
            <p className="flex justify-between"><span className="text-[var(--muted-fg)]">Coupon{order.coupon?.code ? ` (${order.coupon.code})` : ""}</span> <span className="text-[var(--terracotta)]">−{formatPrice(order.discount)}</span></p>
          )}
          <p className="flex justify-between"><span className="text-[var(--muted-fg)]">Shipping</span> <span>{order.shipping_cost === 0 ? "Free" : formatPrice(order.shipping_cost)}</span></p>
          <p className="flex justify-between font-semibold mt-1"><span>Total</span> <span>{formatPrice(order.total)}</span></p>
          {order.refunded_total > 0 && (
            <p className="flex justify-between text-[var(--terracotta)]" data-testid="order-refunded-total"><span>Refunded</span> <span>−{formatPrice(order.refunded_total)}</span></p>
          )}
        </div>
      </div>
    </div>
  );
}

function statusColor(status) {
  switch (status) {
    case "paid": return "bg-emerald-100 text-emerald-800";
    case "shipped":
    case "partially_shipped": return "bg-blue-100 text-blue-800";
    case "delivered": return "bg-emerald-200 text-emerald-900";
    case "cancelled":
    case "expired": return "bg-rose-100 text-rose-800";
    default: return "bg-amber-100 text-amber-900";
  }
}
